import { useState, type ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  generateReport,
  getReport,
  listReports,
  getConfig,
  saveConfig,
  downloadReportPdf,
  type ContentRadarConfig,
  type ContentReport,
  type Platform,
  type WatchedProfile,
} from '../../api/content';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { DataState } from '../../components/DataState';
import { formatDateTime } from '../../components/formatters';
import { palette } from '../../theme';

const PLATFORMS: { value: Platform; label: string }[] = [
  { value: 'x', label: 'X (Twitter)' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'other', label: 'Inne' },
];

const PRESET_INSIGHTS = [
  'top engagement',
  'viral',
  'hooki i openingi',
  'formaty',
  'wzorce contentu',
  'pomysły na content i ads',
  'dlaczego to działa',
  'early signals / trendy',
];

const norm = (s: string) => s.trim().toLowerCase();

export function ContentRadarPage() {
  const [view, setView] = useState<'report' | 'settings'>('report');

  return (
    <Box>
      <PageHeader
        title="Content Radar"
        subtitle="Automatyczny research profilu (Claude + web search) → gotowy raport: tematy, hooki, formaty, pomysły."
      />
      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 3 }}>
        <Tab value="report" label="Raport" />
        <Tab value="settings" label="Ustawienia" />
      </Tabs>
      {view === 'report' ? <ReportTab /> : <SettingsTab />}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Report tab — generate + browse + read
// ---------------------------------------------------------------------------

function ReportTab() {
  const [handle, setHandle] = useState('');
  const [platform, setPlatform] = useState<Platform>('x');
  const [instruction, setInstruction] = useState('');
  const [active, setActive] = useState<ContentReport | null>(null);
  const [newThemes, setNewThemes] = useState<Set<string>>(new Set());
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterHandle, setFilterHandle] = useState('');
  const queryClient = useQueryClient();

  const configQuery = useQuery({ queryKey: ['content', 'config'], queryFn: ({ signal }) => getConfig(signal).then((r) => r.config) });

  const reportsQuery = useQuery({
    queryKey: ['content', 'reports', filterHandle],
    queryFn: ({ signal }) => listReports({ handle: filterHandle || undefined, limit: 50 }, signal).then((r) => r.reports),
  });

  // Compute which executive-summary topThemes are new vs the previous report for
  // the same profile (week-over-week signal).
  async function loadWithDiff(report: ContentReport) {
    setActive(report);
    setNewThemes(new Set());
    try {
      const prev = await listReports({ handle: report.handle, before: report.generatedAt, limit: 1 });
      if (prev.reports.length === 0) return;
      const priorFull = await getReport(prev.reports[0]._id);
      const priorSet = new Set(priorFull.report.executiveSummary.topThemes.map(norm));
      setNewThemes(new Set(report.executiveSummary.topThemes.filter((t) => !priorSet.has(norm(t))).map(norm)));
    } catch {
      // diff is best-effort — ignore
    }
  }

  const generate = useMutation({
    mutationFn: generateReport,
    onSuccess: ({ report }) => {
      setError(null);
      void loadWithDiff(report);
      queryClient.invalidateQueries({ queryKey: ['content', 'reports'] });
    },
    onError: (err: unknown) => setError(err instanceof ApiError ? err.message : 'Nie udało się wygenerować raportu.'),
  });

  async function openReport(id: string) {
    setLoadingId(id);
    setError(null);
    try {
      const { report } = await getReport(id);
      await loadWithDiff(report);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nie udało się wczytać raportu.');
    } finally {
      setLoadingId(null);
    }
  }

  const canGenerate = handle.trim().length > 0 && !generate.isPending;
  const profiles = configQuery.data?.profiles ?? [];

  return (
    <Box>
      <Card sx={{ borderRadius: 3, p: 3, mb: 3 }}>
        <Stack spacing={2}>
          {profiles.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Obserwowane:
              </Typography>
              {profiles.map((p, i) => (
                <Chip
                  key={i}
                  size="small"
                  label={p.handle}
                  variant="outlined"
                  onClick={() => {
                    setHandle(p.handle);
                    setPlatform(p.platform);
                  }}
                />
              ))}
            </Stack>
          )}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Profil (handle / URL)"
              placeholder="np. @bensmithlive"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              fullWidth
              disabled={generate.isPending}
            />
            <TextField
              label="Platforma"
              select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as Platform)}
              sx={{ minWidth: { md: 200 } }}
              disabled={generate.isPending}
            >
              {PLATFORMS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Nadpisz instrukcję adaptacji (opcjonalne)"
            placeholder="Zostaw puste, by użyć wspólnej instrukcji z Ustawień (lub domyślnego kontekstu marki)."
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            disabled={generate.isPending}
          />
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<AutoAwesomeRoundedIcon />}
              disabled={!canGenerate}
              onClick={() =>
                generate.mutate({ handle: handle.trim(), platform, adaptationInstruction: instruction.trim() || undefined })
              }
            >
              {generate.isPending ? 'Generuję… (~30–60s)' : 'Generuj raport'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Raport jest jakościowy — liczby zaangażowania są przybliżone (web search, nie precyzyjna analityka).
            </Typography>
          </Stack>
        </Stack>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
        <Card sx={{ borderRadius: 3, width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="subtitle2">Archiwum</Typography>
          </Stack>
          {profiles.length > 0 && (
            <Box sx={{ px: 2, pb: 1 }}>
              <TextField
                select
                size="small"
                fullWidth
                label="Profil"
                value={filterHandle}
                onChange={(e) => setFilterHandle(e.target.value)}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                {profiles.map((p, i) => (
                  <MenuItem key={i} value={p.handle}>
                    {p.handle}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
          <Divider />
          <DataState
            loading={reportsQuery.isLoading}
            error={reportsQuery.error}
            data={reportsQuery.data}
            isEmpty={(r) => r.length === 0}
            emptyMessage="Brak raportów — wygeneruj pierwszy."
          >
            {(reports) => (
              <List dense disablePadding sx={{ maxHeight: 520, overflowY: 'auto' }}>
                {reports.map((r) => (
                  <ListItem key={r._id} disablePadding>
                    <ListItemButton selected={active?._id === r._id} onClick={() => openReport(r._id)} disabled={loadingId === r._id}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {r.handle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(r.generatedAt)}
                          {r.status === 'unavailable' ? ' · niedostępny' : ''}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </DataState>
        </Card>

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {active ? (
            <ReportView report={active} newThemes={newThemes} />
          ) : (
            <Card sx={{ borderRadius: 3, p: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Wygeneruj raport albo wybierz jeden z archiwum, aby go tutaj zobaczyć.
              </Typography>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Settings tab — shared team config
// ---------------------------------------------------------------------------

function SettingsTab() {
  const configQuery = useQuery({ queryKey: ['content', 'config'], queryFn: ({ signal }) => getConfig(signal).then((r) => r.config) });
  return (
    <DataState loading={configQuery.isLoading} error={configQuery.error} data={configQuery.data}>
      {(config) => <ConfigEditor config={config} />}
    </DataState>
  );
}

function ConfigEditor({ config }: { config: ContentRadarConfig }) {
  const [profiles, setProfiles] = useState<WatchedProfile[]>(config.profiles);
  const [insightTypes, setInsightTypes] = useState<string[]>(config.insightTypes);
  const [adaptation, setAdaptation] = useState(config.adaptationInstruction);
  const [cadence, setCadence] = useState(config.cadence);
  const [activeFlag, setActiveFlag] = useState(config.active);
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: saveConfig,
    onSuccess: () => {
      setFeedback({ severity: 'success', message: 'Zapisano ustawienia.' });
      queryClient.invalidateQueries({ queryKey: ['content', 'config'] });
      queryClient.invalidateQueries({ queryKey: ['content', 'reports'] });
    },
    onError: (err: unknown) => setFeedback({ severity: 'error', message: err instanceof ApiError ? err.message : 'Nie udało się zapisać.' }),
  });

  const insightOptions = Array.from(new Set([...PRESET_INSIGHTS, ...insightTypes]));
  const toggleInsight = (t: string) =>
    setInsightTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const updateProfile = (i: number, patch: Partial<WatchedProfile>) =>
    setProfiles((cur) => cur.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  return (
    <Box sx={{ maxWidth: 820 }}>
      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <Card sx={{ borderRadius: 3, p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          Harmonogram
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <FormControlLabel
            control={<Switch checked={activeFlag} onChange={(e) => setActiveFlag(e.target.checked)} />}
            label="Automatyczne generowanie aktywne"
          />
          <TextField select size="small" label="Cykl" value={cadence} onChange={(e) => setCadence(e.target.value as ContentRadarConfig['cadence'])} sx={{ minWidth: 180 }}>
            <MenuItem value="weekly">Co tydzień</MenuItem>
            <MenuItem value="biweekly">Co dwa tygodnie</MenuItem>
          </TextField>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Scheduler w tle regeneruje profil, gdy jego ostatni raport jest starszy niż cykl. Wymaga CONTENT_RADAR_ENABLED=true po stronie api.
        </Typography>
      </Card>

      <Card sx={{ borderRadius: 3, p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          Typy insightów
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {insightOptions.map((t) => {
            const on = insightTypes.includes(t);
            return (
              <Chip
                key={t}
                label={t}
                onClick={() => toggleInsight(t)}
                variant={on ? 'filled' : 'outlined'}
                sx={on ? { bgcolor: palette.sage[500], color: 'white', '&:hover': { bgcolor: palette.sage[700] } } : undefined}
              />
            );
          })}
        </Stack>
      </Card>

      <Card sx={{ borderRadius: 3, p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Instrukcja adaptacji (wspólna)
        </Typography>
        <TextField
          value={adaptation}
          onChange={(e) => setAdaptation(e.target.value)}
          placeholder="Jak AI ma przekładać techniki na natu.care. Puste = domyślny kontekst marki (kolagen, kobiety 30+, Hashimoto, founderka Natalia…)."
          multiline
          minRows={3}
          fullWidth
        />
      </Card>

      <Card sx={{ borderRadius: 3, p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Obserwowane profile
          </Typography>
          <Button
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setProfiles((cur) => [...cur, { handle: '', platform: 'x', note: '' }])}
          >
            Dodaj
          </Button>
        </Stack>
        {profiles.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Brak profili. Dodaj profile, które radar ma obserwować.
          </Typography>
        )}
        <Stack spacing={2}>
          {profiles.map((p, i) => (
            <Stack key={i} direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                label="Handle / URL"
                size="small"
                value={p.handle}
                onChange={(e) => updateProfile(i, { handle: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Platforma"
                select
                size="small"
                value={p.platform}
                onChange={(e) => updateProfile(i, { platform: e.target.value as Platform })}
                sx={{ minWidth: 150 }}
              >
                {PLATFORMS.map((pl) => (
                  <MenuItem key={pl.value} value={pl.value}>
                    {pl.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Notatka (opcjonalnie)"
                size="small"
                value={p.note}
                onChange={(e) => updateProfile(i, { note: e.target.value })}
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => setProfiles((cur) => cur.filter((_, idx) => idx !== i))} aria-label="usuń profil">
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Card>

      <Button
        variant="contained"
        disabled={save.isPending}
        onClick={() =>
          save.mutate({
            profiles: profiles.map((p) => ({ ...p, handle: p.handle.trim() })).filter((p) => p.handle.length > 0),
            insightTypes,
            adaptationInstruction: adaptation,
            cadence,
            active: activeFlag,
          })
        }
      >
        {save.isPending ? 'Zapisuję…' : 'Zapisz ustawienia'}
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Report reader
// ---------------------------------------------------------------------------

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Skopiowano' : 'Kopiuj'}>
      <IconButton
        size="small"
        onClick={() => {
          void navigator.clipboard?.writeText(text);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }}
      >
        <ContentCopyRoundedIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

function Section({ title, count, children, defaultExpanded }: { title: string; count?: number; children: ReactNode; defaultExpanded?: boolean }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, mb: 1.5, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
          {count != null && count > 0 && (
            <Chip size="small" label={count} sx={{ ml: 1, bgcolor: palette.sage[100], color: palette.sage[700] }} />
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

function Bullets({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <Box component="ul" sx={{ pl: 3, m: 0 }}>
      {items.map((t, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
          {t}
        </Typography>
      ))}
    </Box>
  );
}

function InfoCard({ children }: { children: ReactNode }) {
  return <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mb: 1.5 }}>{children}</Box>;
}

function ReportView({ report, newThemes }: { report: ContentReport; newThemes?: Set<string> }) {
  const r = report;
  const isNew = (t: string) => newThemes?.has(t.trim().toLowerCase()) ?? false;
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await downloadReportPdf(r._id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `content-radar-${r.handle.replace(/[^a-z0-9_-]+/gi, '_')}-${r.periodTo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError('Nie udało się wyeksportować PDF. Spróbuj ponownie.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 3, p: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {r.handle}
        </Typography>
        <Chip size="small" label={r.platform} variant="outlined" />
        {r.status === 'unavailable' ? (
          <Chip size="small" label="niedostępny" sx={{ bgcolor: '#FEF2F2', color: '#991B1B' }} />
        ) : (
          <Chip size="small" label={r.source === 'llm' ? 'AI' : r.source} sx={{ bgcolor: palette.sage[100], color: palette.sage[700] }} />
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={downloading ? <CircularProgress size={14} /> : <PictureAsPdfRoundedIcon />}
          sx={{ ml: 'auto' }}
          disabled={r.status === 'unavailable' || downloading}
          onClick={() => void handleDownload()}
        >
          {downloading ? 'Generowanie…' : 'Eksportuj PDF'}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {r.periodFrom} → {r.periodTo} · wygenerowano {formatDateTime(r.generatedAt)}
        {r.model ? ` · ${r.model}` : ''}
      </Typography>

      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      {r.status === 'unavailable' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Raport nie został wygenerowany.{r.statusReason ? ` (${r.statusReason})` : ''} Spróbuj ponownie za chwilę.
        </Alert>
      )}

      {r.profileContext && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {r.profileContext}
        </Typography>
      )}

      <Section title="Executive summary" defaultExpanded>
        {r.executiveSummary.topThemes.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Top tematy</Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {r.executiveSummary.topThemes.map((t, i) => (
                <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {t}
                  {isNew(t) && <Chip size="small" label="nowe" sx={{ ml: 1, bgcolor: palette.sage[100], color: palette.sage[700] }} />}
                </Typography>
              ))}
            </Box>
          </>
        )}
        {r.executiveSummary.workingFormats.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>Formaty które działają</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {r.executiveSummary.workingFormats.map((f, i) => (
                <Chip key={i} size="small" label={f} variant="outlined" />
              ))}
            </Stack>
          </>
        )}
        {r.executiveSummary.engagementPattern && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            {r.executiveSummary.engagementPattern}
          </Typography>
        )}
      </Section>

      <Section title="Top tematy" count={r.topThemes.length}>
        {r.topThemes.map((t, i) => (
          <InfoCard key={i}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip size="small" label={`#${t.rank}`} sx={{ bgcolor: palette.sage[100], color: palette.sage[700] }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t.title}</Typography>
              {t.bestChannel && <Chip size="small" label={t.bestChannel} variant="outlined" />}
            </Stack>
            {t.examples.length > 0 && <Bullets items={t.examples} />}
            {t.whyItWorks && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <b>Dlaczego działa:</b> {t.whyItWorks}
              </Typography>
            )}
            {t.adaptationForNatuCare && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <b>Dla natu.care:</b> {t.adaptationForNatuCare}
              </Typography>
            )}
          </InfoCard>
        ))}
      </Section>

      <Section title="Wzorce contentu" count={r.contentPatterns.length}>
        {r.contentPatterns.map((p, i) => (
          <InfoCard key={i}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{p.name}</Typography>
            {p.whatItIs && <Typography variant="body2"><b>Na czym polega:</b> {p.whatItIs}</Typography>}
            {p.whyItWorks && <Typography variant="body2" sx={{ mt: 0.5 }}><b>Czemu działa:</b> {p.whyItWorks}</Typography>}
            {p.howForUs && <Typography variant="body2" sx={{ mt: 0.5 }}><b>Jak u nas:</b> {p.howForUs}</Typography>}
          </InfoCard>
        ))}
      </Section>

      <Section
        title="Pomysły na content"
        count={r.opportunities.youtube.length + r.opportunities.meta.length + r.opportunities.shortForm.length}
       
      >
        {r.opportunities.youtube.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>YouTube</Typography>
            {r.opportunities.youtube.map((o, i) => (
              <InfoCard key={i}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{o.title}</Typography>
                  <CopyButton text={o.title} />
                </Stack>
                {o.description && <Typography variant="body2" sx={{ mt: 0.5 }}>{o.description}</Typography>}
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  {o.format && <Chip size="small" label={o.format} variant="outlined" />}
                  {o.insight && <Chip size="small" label={o.insight} sx={{ bgcolor: palette.sage[100], color: palette.sage[700] }} />}
                </Stack>
              </InfoCard>
            ))}
          </>
        )}
        {r.opportunities.meta.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Meta</Typography>
            {r.opportunities.meta.map((o, i) => (
              <OpportunityHookCard key={i} hook={o.hook} format={o.format} insight={o.insight} />
            ))}
          </>
        )}
        {r.opportunities.shortForm.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Short-form</Typography>
            {r.opportunities.shortForm.map((o, i) => (
              <OpportunityHookCard key={i} hook={o.hook} format={o.format} insight={o.insight} />
            ))}
          </>
        )}
      </Section>

      {(r.ugcIdeas ?? []).length > 0 && (
        <Section title="Pomysły na UGC" count={(r.ugcIdeas ?? []).length}>
          {(r.ugcIdeas ?? []).map((u, i) => (
            <InfoCard key={i}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{u.title}</Typography>
                <CopyButton text={u.title} />
              </Stack>
              {u.description && <Typography variant="body2" sx={{ mt: 0.5 }}>{u.description}</Typography>}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {u.format && <Chip size="small" label={u.format} variant="outlined" />}
                {u.insight && <Chip size="small" label={u.insight} sx={{ bgcolor: palette.sage[100], color: palette.sage[700] }} />}
              </Stack>
            </InfoCard>
          ))}
        </Section>
      )}

      <Section title="Hooki / opening lines" count={r.hooks.length}>
        <Stack spacing={1}>
          {r.hooks.map((h, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{h}</Typography>
              <CopyButton text={h} />
            </Box>
          ))}
        </Stack>
      </Section>

      <Section title="Priorytety" count={r.priorities.length}>
        {r.priorities.map((p, i) => (
          <InfoCard key={i}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip size="small" label={`#${p.rank}`} sx={{ bgcolor: palette.sage[100], color: palette.sage[700] }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{p.title}</Typography>
            </Stack>
            {p.why && <Typography variant="body2"><b>Dlaczego:</b> {p.why}</Typography>}
            {p.format && <Typography variant="body2" sx={{ mt: 0.5 }}><b>Format:</b> {p.format}</Typography>}
            {p.adaptation && <Typography variant="body2" sx={{ mt: 0.5 }}><b>Adaptacja:</b> {p.adaptation}</Typography>}
          </InfoCard>
        ))}
      </Section>

      <Section title="Strategic takeaways">
        {r.strategicTakeaways.whatThisProfileTeaches.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Czego uczy ten profil</Typography>
            <Bullets items={r.strategicTakeaways.whatThisProfileTeaches} />
          </>
        )}
        {r.strategicTakeaways.whatBrandsMiss.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>Czego brakuje markom</Typography>
            <Bullets items={r.strategicTakeaways.whatBrandsMiss} />
          </>
        )}
        {r.strategicTakeaways.lessonsToImplement.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>Lekcje do wdrożenia</Typography>
            <Bullets items={r.strategicTakeaways.lessonsToImplement} />
          </>
        )}
      </Section>

      {r.earlySignals.length > 0 && (
        <Section title="Early signals / trendy" count={r.earlySignals.length}>
          <Bullets items={r.earlySignals} />
        </Section>
      )}

      {r.sources.length > 0 && (
        <Section title="Źródła" count={r.sources.length}>
          <Stack spacing={0.5}>
            {r.sources.map((s, i) => (
              <Link key={i} href={s.url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {s.note || s.url}
                <OpenInNewRoundedIcon fontSize="inherit" />
              </Link>
            ))}
          </Stack>
        </Section>
      )}
    </Card>
  );
}

function OpportunityHookCard({ hook, format, insight }: { hook: string; format: string; insight: string }) {
  return (
    <InfoCard>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{hook}</Typography>
        <CopyButton text={hook} />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
        {format && <Chip size="small" label={format} variant="outlined" />}
        {insight && <Chip size="small" label={insight} sx={{ bgcolor: palette.sage[100], color: palette.sage[700] }} />}
      </Stack>
    </InfoCard>
  );
}
